import React, { useState, useEffect } from 'react';
import Modal, { ModalCloseButton } from './Modal';
import { apiFetch } from '../api';

const RANK_BADGE = [
  { bg: '#fbbf24', fg: '#78350f' },  // Gold
  { bg: '#d1d5db', fg: '#374151' },  // Silber
  { bg: '#d97706', fg: 'white' },    // Bronze
];

export default function PerformersModal({ onClose, authHeader, currentUser }) {
  const [period, setPeriod] = useState('week');
  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    setLoadError(false);
    apiFetch('/api/leads/stats/performers', { headers: authHeader })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(setData)
      .catch(() => setLoadError(true));
  }, [authHeader]);

  const fullList = data ? (period === 'today' ? data.today : period === 'week' ? data.week : data.month) : [];
  // Nur die ersten drei Plätze anzeigen
  const list = fullList.slice(0, 3);
  const max = list[0]?.count || 1;

  // Eigene Leistung des eingeloggten Vertrieblers — unabhängig von der Platzierung
  const showPersonal = currentUser && currentUser.role !== 'admin';
  const myIndex = showPersonal ? fullList.findIndex(r => r.username === currentUser.username) : -1;
  const myRank  = myIndex >= 0 ? myIndex + 1 : null;
  const myCount = myIndex >= 0 ? fullList[myIndex].count : 0;
  const myBadge = myRank && RANK_BADGE[myRank - 1];

  return (
    <Modal onClose={onClose} label="Top Performer">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2>Top Performer</h2>
          <ModalCloseButton onClose={onClose} />
        </div>

        <div role="group" aria-label="Zeitraum auswählen" style={{ display: 'flex', gap: 4, marginBottom: 20, background: '#f5f3ff', padding: 4, borderRadius: 12 }}>
          {[['today', 'Heute'], ['week', 'Woche'], ['month', 'Monat']].map(([key, label]) => (
            <button key={key} onClick={() => setPeriod(key)} aria-pressed={period === key} style={{
              flex: 1, padding: '8px 0', border: 'none', borderRadius: 8, cursor: 'pointer',
              fontFamily: 'inherit', fontSize: 13, fontWeight: 600,
              background: period === key ? 'white' : 'transparent',
              color: period === key ? '#5b21b6' : '#6b7280',
              boxShadow: period === key ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
              transition: 'all 0.15s',
              minHeight: 40,
            }}>{label}</button>
          ))}
        </div>

        {!data && !loadError && <p style={{ textAlign: 'center', color: '#6b7280', padding: '24px 0' }}>Lädt...</p>}

        {loadError && (
          <div className="login-error" role="alert" style={{ textAlign: 'center' }}>
            Daten konnten nicht geladen werden — bitte Modal schließen und erneut öffnen.
          </div>
        )}

        {data && list.length === 0 && (
          <p style={{ textAlign: 'center', color: '#6b7280', padding: '32px 0', fontSize: 14 }}>
            Noch keine Abschlüsse in diesem Zeitraum.
          </p>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {(list || []).map((row, i) => (
            <div key={row.username} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12,
              background: i === 0 ? '#fefce8' : '#f9f8ff',
              border: `1px solid ${i === 0 ? '#fde047' : '#ede9fe'}`,
            }}>
              <span aria-label={`Platz ${i + 1}`} style={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 14, fontWeight: 800,
                background: RANK_BADGE[i]?.bg ?? '#ede9fe',
                color: RANK_BADGE[i]?.fg ?? '#5b21b6',
                boxShadow: i < 3 ? '0 1px 4px rgba(0,0,0,0.18)' : 'none',
              }}>
                {i + 1}
              </span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: '#1e1b4b', marginBottom: 5 }}>{row.username}</div>
                <div style={{ height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', width: `${(row.count / max) * 100}%`,
                    background: 'linear-gradient(90deg, #3b3be8, #a855f7)',
                    borderRadius: 3, transition: 'width 0.5s ease',
                  }} />
                </div>
              </div>
              <span style={{ fontWeight: 800, fontSize: 22, color: '#5b21b6', minWidth: 32, textAlign: 'right' }}>
                {row.count}
              </span>
            </div>
          ))}
        </div>

        {data && showPersonal && (
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid #ede9fe' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>
              Deine Leistung
            </div>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', borderRadius: 12,
              background: '#eef2ff', border: '1px solid #c7d2fe',
            }}>
              <span aria-label={myRank ? `Platz ${myRank}` : 'Keine Platzierung'} style={{
                width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 13, fontWeight: 800,
                background: myBadge ? myBadge.bg : '#ede9fe',
                color: myBadge ? myBadge.fg : '#5b21b6',
              }}>
                {myRank ?? '—'}
              </span>
              <div style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 14, color: '#1e1b4b' }}>
                {currentUser.username}
                {myRank > 3 && <span style={{ fontWeight: 500, color: '#6b7280', fontSize: 13 }}> · Platz {myRank}</span>}
              </div>
              <span style={{ fontWeight: 800, fontSize: 22, color: '#5b21b6', minWidth: 32, textAlign: 'right' }}>
                {myCount}
              </span>
            </div>
            {myCount === 0 && (
              <p style={{ textAlign: 'center', color: '#6b7280', fontSize: 13, margin: '10px 0 0' }}>
                Noch keine Abschlüsse in diesem Zeitraum.
              </p>
            )}
          </div>
        )}
    </Modal>
  );
}
